<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center flex-wrap gap-2">
              <h6 class="mb-0 me-auto">Trabajos de Operarios</h6>
              <div v-if="!isOperario" class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="openCreateModal"
                  title="Agregar Trabajo"
                >
                  <i class="fa fa-plus me-1"></i> Agregar
                </button>
                <button class="btn btn-sm btn-success" @click="exportToExcel" title="Exportar a Excel">
                  <i class="fas fa-file-excel me-1"></i> Exportar Excel
                </button>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <!-- Filtros -->
            <div class="px-4 pt-3 pb-2">
              <div class="row g-2">
                <div class="col-md-3" v-if="!isFieldWorker">
                  <label class="form-control-label">Usuario</label>
                  <select class="form-select" v-model="filters.user_id" @change="fetchData()">
                    <option value="">Todos</option>
                    <option v-for="user in operarioUsers" :key="user.id" :value="user.id">{{ user.name }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Estado</label>
                  <select class="form-select" v-model="filters.status" @change="fetchData()">
                    <option value="">Todos</option>
                    <option value="pending">Pendiente</option>
                    <option value="paid">Pagado</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Fecha desde</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="filters.start_date"
                    @change="fetchData()"
                  />
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">Fecha hasta</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="filters.end_date"
                    @change="fetchData()"
                  />
                </div>
                <div class="col-md-12">
                  <label class="form-control-label">Buscar</label>
                  <input type="text" class="form-control" v-model="filters.search" placeholder="Buscar por descripción..." @input="fetchData()" />
                </div>
              </div>
            </div>

            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Operario</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descripción</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Tipo</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio Total</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">{{ employeeEarningLabel }}</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pagado al empleado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="data && data.length > 0" class="bg-light">
                    <td colspan="4" class="text-end align-middle text-sm font-weight-bold">
                      Total a Pagar (Pendientes):
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm font-weight-bold text-warning">
                        {{ totalPendingEmployeeEarning.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm font-weight-bold text-success">
                        {{ totalPaidEmployeeEarning.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td colspan="3" class="align-middle text-center" >
                      <button
                          v-if="!isFieldWorker && pendingTasksCount > 0"
                          type="button"
                          @click="markAllPendingAsPaid"
                          class="btn btn-sm btn-info mb-0"
                          title="Marcar todas las tareas pendientes como pagadas"
                        >
                          <i class="fas fa-check me-1"></i>
                          Marcar todas como pagadas
                        </button>
                    </td>
                  </tr>
                  <tr v-for="task in data" :key="task.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <h6 class="mb-0 text-sm">{{ task.user?.name || 'N/A' }}</h6>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <p class="text-xs mb-0" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
                          {{ task.description }}
                        </p>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span class="badge badge-sm" :class="{
                        'bg-gradient-info': task.type === 'task',
                        'bg-gradient-warning': task.type === 'debt'
                      }">
                        {{ task.type === 'task' ? 'Tarea' : 'Deuda' }}
                      </span>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm">
                        {{ parseFloat(task.price ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm">
                        {{ employeeEarning(task).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm text-success">
                        {{ parseFloat(task.employee_paid ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                      </h6>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <span class="badge badge-sm" :class="{
                        'bg-gradient-warning': task.status === 'pending',
                        'bg-gradient-info': task.status === 'partial',
                        'bg-gradient-success': task.status === 'paid'
                      }">
                        {{
                          task.status === 'pending' ? 'Pendiente' :
                          task.status === 'partial' ? 'Parcial' :
                          task.status === 'paid' ? 'Pagado' : ''
                        }}
                      </span>
                    </td>
                    <td class="align-middle text-center text-sm">
                      <h6 class="mb-0 text-sm">
                        {{ formatDateOnly(task.task_date) || 'Sin fecha' }}
                      </h6>
                    </td>
                    <td class="align-middle text-center">
                      <a
                        v-if="!isFieldWorker && task.status !== 'paid'"
                        @click="markAsPaid(task)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer me-3"
                        title="Marcar como pagado"
                      >
                        <i class="fas fa-check text-secondary"></i>
                      </a>
                      <a
                        v-if="!isOperario && task.status !== 'paid'"
                        @click="deleteTask(task.id)"
                        class="text-secondary font-weight-bold text-xs cursor-pointer"
                        title="Eliminar"
                      >
                        <i class="fas fa-trash text-danger"></i>
                      </a>
                    </td>
                  </tr>
                  <tr v-if="!data || data.length === 0">
                    <td colspan="8" class="text-center text-xxs">No hay registros disponibles</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="pagination p-3">
              <a
                v-for="link in links"
                :key="link.label"
                class="page-item cursor-pointer"
                @click="fetchData(link.label)"
                :disabled="!link.url || link.active"
                :class="{ 'active': link.label === currentPage.toString() }"
              >
                {{ link.label.replace('&laquo;', '«').replace('&raquo;', '»') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal agregar / editar trabajo -->
  <div class="modal fade" id="ModalWorkerTask" tabindex="-1" aria-labelledby="workerTaskModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content card">
        <div class="modal-header">
          <h5 class="modal-title" id="workerTaskModalLabel">
            {{ taskForm.id ? 'Editar Trabajo' : 'Agregar Trabajo' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar" @click="closeTaskModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitTaskForm">
            <div v-if="!isFieldWorker" class="mb-3">
              <label class="form-label">Operario *</label>
              <select ref="operarioSelect" class="form-select" style="width: 100%;">
                <option value="">Seleccione un operario</option>
                <option v-for="user in operarioUsers" :key="user.id" :value="user.id">
                  {{ user.name }}
                </option>
              </select>
            </div>
            <div v-else class="mb-3">
              <label class="form-label">Operario</label>
              <input type="text" class="form-control" :value="currentUser.name" readonly>
            </div>

            <div class="mb-3">
              <label class="form-label">Descripción *</label>
              <textarea
                v-model="taskForm.description"
                class="form-control"
                rows="3"
                placeholder="Descripción del trabajo"
                required
              ></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Precio *</label>
              <input
                type="text"
                inputmode="decimal"
                class="form-control"
                placeholder="0,00"
                :value="formatCurrencyInput(taskForm.price)"
                @input="onTaskPriceInput"
              >
            </div>

            <div class="mb-3">
              <label class="form-label">% pagado al empleado</label>
              <input
                type="number"
                v-model.number="taskForm.employee_percent"
                class="form-control"
                min="0"
                max="100"
                step="0.01"
                @input="syncEmployeeAmountFromPercent"
              >
            </div>

            <div class="mb-3">
              <label class="form-label">Valor a pagar al empleado</label>
              <input
                type="text"
                inputmode="decimal"
                class="form-control"
                placeholder="0,00"
                :value="formatCurrencyInput(taskForm.employee_amount)"
                @input="onTaskEmployeeAmountInput"
              >
            </div>

            <div class="mb-3">
              <label class="form-label">Fecha</label>
              <input type="date" v-model="taskForm.task_date" class="form-control">
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeTaskModal">Cerrar</button>
          <button type="button" class="btn btn-primary" :disabled="savingTask" @click="submitTaskForm">
            {{ savingTask ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Swal from 'sweetalert2';
import { Modal } from 'bootstrap';
import $ from 'jquery';
import 'select2/dist/css/select2.css';
import 'select2';
import userService from '@/services/user.service';
import { formatDateOnly } from '@/helpers/dateFormat';
import { formatCurrencyInput, parseCurrencyInput } from '@/helpers/currencyInput';

const todayDateString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default {
  name: 'WorkerTasksTable',
  data() {
    const today = todayDateString();
    return {
      filters: {
        user_id: '',
        status: '',
        search: '',
        start_date: today,
        end_date: today,
      },
      users: [],
      modalTask: null,
      savingTask: false,
      taskForm: {
        id: 0,
        user_id: '',
        description: '',
        price: 0,
        employee_percent: 70,
        employee_amount: 0,
        type: 'task',
        task_date: new Date().toISOString().split('T')[0],
      },
    };
  },
  computed: {
    ...mapGetters('workerTasks', ['allData', 'currentPage', 'lastPage', 'pagination', 'links']),
    ...mapGetters('settings', ['employeeCommissionPercent', 'employeeCommissionRate']),
    employeeEarningLabel() {
      return `Ganancia Empleado (${this.employeeCommissionPercent}%)`;
    },
    defaultEmployeePercent() {
      return this.employeeCommissionPercent;
    },
    currentPage() {
      return this.$store.state.workerTasks?.currentPage || 1;
    },
    lastPage() {
      return this.$store.state.workerTasks?.lastPage || 1;
    },
    data() {
      return this.allData || [];
    },
    links() {
      return this.$store.getters['workerTasks/links'] || [];
    },
    totalPendingEmployeeEarning() {
      if (!this.data || this.data.length === 0) {
        return 0;
      }
      return this.data
        .filter(task => task.status === 'pending' || task.status === 'partial')
        .reduce((total, task) => total + this.employeeBalance(task), 0);
    },
    totalPaidEmployeeEarning() {
      if (!this.data || this.data.length === 0) {
        return 0;
      }
      return this.data.reduce((total, task) => {
        return total + parseFloat(task.employee_paid ?? 0);
      }, 0);
    },
    pendingTasksCount() {
      if (!this.data || this.data.length === 0) {
        return 0;
      }
      return this.data.filter(task => task.status === 'pending').length;
    },
    currentUser() {
      try {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    },
    userRole() {
      return String(this.currentUser?.role ?? '').trim();
    },
    isOperario() {
      return this.userRole === '3';
    },
    isFieldWorker() {
      return this.isOperario || this.userRole === '6';
    },
    operarioUsers() {
      const allowedRoles = ['1', '3', '6'];
      return this.users.filter((user) => {
        const role = String(user.role ?? '').trim();
        return allowedRoles.includes(role);
      });
    },
  },
  methods: {
    formatCurrencyInput,
    parseCurrencyInput,
    formatDateOnly,
    employeeEarning(task) {
      const price = parseFloat(task?.price ?? 0);
      return Math.round(price * this.employeeCommissionRate * 100) / 100;
    },
    employeeBalance(task) {
      return Math.max(0, this.employeeEarning(task) - parseFloat(task?.employee_paid ?? 0));
    },
    appendListFilters(params) {
      if (this.filters.user_id) {
        params.user_id = this.filters.user_id;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      if (this.filters.search) {
        params.search = this.filters.search;
      }
      if (this.filters.start_date) {
        params.start_date = this.filters.start_date;
      }
      if (this.filters.end_date) {
        params.end_date = this.filters.end_date;
      }
      return params;
    },
    async fetchData(pageUrl = null) {
      const params = { per_page: 50 };
      this.appendListFilters(params);
      
      // Manejar paginación - si pageUrl es un número, usarlo como página
      if (pageUrl && typeof pageUrl === 'number') {
        params.page = pageUrl;
      } else if (pageUrl && typeof pageUrl === 'string' && pageUrl !== 'all') {
        // Si es una URL, extraer el número de página
        const pageMatch = pageUrl.match(/page=(\d+)/);
        if (pageMatch) {
          params.page = pageMatch[1];
        } else {
          // Si es solo un número como string
          const pageNum = parseInt(pageUrl);
          if (!isNaN(pageNum)) {
            params.page = pageNum;
          }
        }
      }
      
      await this.$store.dispatch('workerTasks/fetchData', params);
    },
    async fetchUsers() {
      try {
        const response = await userService.getAll('all');
        const allUsers = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        this.users = allUsers;
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    isValidDate(date) {
      if (!date) return false;
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    },
    resetTaskForm() {
      const today = new Date().toISOString().split('T')[0];
      this.taskForm = {
        id: 0,
        user_id: this.isFieldWorker ? (this.currentUser.id || '') : '',
        description: '',
        price: 0,
        employee_percent: this.defaultEmployeePercent,
        employee_amount: 0,
        type: 'task',
        task_date: today,
      };
    },
    openCreateModal() {
      this.resetTaskForm();
      this.openTaskModal();
    },
    openTaskModal() {
      const modalElement = document.getElementById('ModalWorkerTask');
      if (!this.modalTask) {
        this.modalTask = new Modal(modalElement);
      }
      this.modalTask.show();
      if (!this.isFieldWorker) {
        this.$nextTick(() => {
          this.initOperarioSelect2();
        });
      }
    },
    closeTaskModal() {
      this.destroyOperarioSelect2();
      if (this.modalTask) {
        this.modalTask.hide();
      }
    },
    initOperarioSelect2() {
      if (this.isFieldWorker || !this.$refs.operarioSelect) {
        return;
      }
      const select = $(this.$refs.operarioSelect);
      if (select.hasClass('select2-hidden-accessible')) {
        select.select2('destroy');
      }
      select.off('change.workerTaskOperario');
      select.select2({
        placeholder: 'Seleccione un operario',
        allowClear: true,
        width: '100%',
        dropdownParent: $('#ModalWorkerTask .modal-body'),
      });
      select.on('change.workerTaskOperario', (e) => {
        this.taskForm.user_id = e.target.value;
      });
      if (this.taskForm.user_id) {
        select.val(String(this.taskForm.user_id)).trigger('change');
      } else {
        select.val(null).trigger('change');
      }
    },
    destroyOperarioSelect2() {
      if (!this.$refs.operarioSelect) {
        return;
      }
      const select = $(this.$refs.operarioSelect);
      if (select.hasClass('select2-hidden-accessible')) {
        select.off('change.workerTaskOperario');
        select.select2('destroy');
      }
    },
    syncEmployeeAmountFromPercent() {
      const price = parseFloat(this.taskForm.price) || 0;
      const pct = parseFloat(this.taskForm.employee_percent);
      if (!Number.isFinite(price) || price <= 0) {
        this.taskForm.employee_amount = 0;
        return;
      }
      const safePct = Number.isFinite(pct) ? pct : this.defaultEmployeePercent;
      this.taskForm.employee_amount = Math.round(price * safePct) / 100;
    },
    syncPercentFromAmount() {
      const price = parseFloat(this.taskForm.price) || 0;
      const amount = parseFloat(this.taskForm.employee_amount) || 0;
      if (price > 0) {
        this.taskForm.employee_percent = Math.round((amount / price) * 10000) / 100;
      }
    },
    onTaskPriceInput(event) {
      this.taskForm.price = parseCurrencyInput(event.target.value);
      this.syncEmployeeAmountFromPercent();
      event.target.value = formatCurrencyInput(this.taskForm.price);
    },
    onTaskEmployeeAmountInput(event) {
      this.taskForm.employee_amount = parseCurrencyInput(event.target.value);
      this.syncPercentFromAmount();
      event.target.value = formatCurrencyInput(this.taskForm.employee_amount);
    },
    async submitTaskForm() {
      const description = String(this.taskForm.description || '').trim();
      const price = parseFloat(this.taskForm.price) || 0;
      const employeeAmount = parseFloat(this.taskForm.employee_amount) || 0;
      const userId = this.isFieldWorker
        ? parseInt(this.currentUser.id, 10)
        : parseInt(this.taskForm.user_id, 10);

      if (!userId) {
        await Swal.fire('Error', 'Debe seleccionar un operario', 'error');
        return;
      }
      if (!description) {
        await Swal.fire('Error', 'La descripción es requerida', 'error');
        return;
      }
      if (!Number.isFinite(price) || price <= 0) {
        await Swal.fire('Error', 'El precio debe ser un número válido y positivo', 'error');
        return;
      }
      if (!Number.isFinite(employeeAmount) || employeeAmount < 0) {
        await Swal.fire('Error', 'El valor a pagar al empleado debe ser válido', 'error');
        return;
      }
      const maxEmployee = Math.round(price * this.employeeCommissionRate * 100) / 100;
      if (employeeAmount > maxEmployee + 0.01) {
        await Swal.fire('Error', 'El valor al empleado no puede ser mayor a la ganancia configurada', 'error');
        return;
      }

      const payload = {
        id: this.taskForm.id || 0,
        user_id: userId,
        type: this.taskForm.type || 'task',
        description,
        price,
        total_paid: 0,
        employee_paid: 0,
        task_date: this.taskForm.task_date || new Date().toISOString().split('T')[0],
      };

      this.savingTask = true;
      try {
        const response = await this.$store.dispatch('workerTasks/save', payload);
        if (response && response.status >= 200 && response.status < 300) {
          this.closeTaskModal();
          await Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: this.taskForm.id
              ? 'Trabajo/deuda actualizado correctamente'
              : 'Trabajo/deuda creado correctamente',
            confirmButtonText: 'Aceptar',
          });
          await this.fetchData(null);
        } else {
          throw new Error(response?.data?.message || response?.data?.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('Error saving task:', error);
        const errorMsg = error.response?.data?.message || error.message || 'No se pudo guardar el trabajo/deuda';
        await Swal.fire('Error', errorMsg, 'error');
      } finally {
        this.savingTask = false;
      }
    },
    async markAsPaid(task) {
      if (task.status === 'paid') {
        // Si ya está pagado y se desmarca, preguntar si quiere desmarcar
        const result = await Swal.fire({
          title: '¿Desmarcar como pagado?',
          text: 'El trabajo/deuda volverá a estado pendiente',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, desmarcar',
          cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed) {
          try {
            const updateData = {
              id: task.id,
              status: 'pending',
              total_paid: 0,
              employee_paid: 0,
            };
            const response = await this.$store.dispatch('workerTasks/save', updateData);
            if (response && response.status >= 200 && response.status < 300) {
              Swal.fire('Éxito', 'Estado actualizado correctamente', 'success');
              await this.fetchData(null);
            } else {
              throw new Error(response?.data?.message || 'Error al actualizar');
            }
          } catch (error) {
            console.error('Error updating task:', error);
            Swal.fire('Error', 'No se pudo actualizar el estado', 'error');
            // Recargar para revertir el checkbox
            await this.fetchData(null);
          }
        } else {
          // Si cancela, recargar para mantener el estado
          await this.fetchData(null);
        }
      } else {
        // Si no está pagado y se marca, confirmar
        const result = await Swal.fire({
          title: '¿Marcar como pagado?',
          html: `
            <div style="text-align: left;">
              <p><strong>Operario:</strong> ${task.user?.name || 'N/A'}</p>
              <p><strong>Descripción:</strong> ${task.description}</p>
              <p><strong>Precio Total:</strong> ${parseFloat(task.price ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p><strong>Ganancia Empleado (${this.employeeCommissionPercent}%):</strong> ${this.employeeEarning(task).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, marcar como pagado',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#28a745',
        });

        if (result.isConfirmed) {
          try {
            // Al marcar como pagado, total_paid debe ser igual al precio (pago completo)
            // El backend calculará automáticamente el estado como 'paid'
            const updateData = {
              id: task.id,
              total_paid: parseFloat(task.price),
              employee_paid: this.employeeEarning(task),
            };
            const response = await this.$store.dispatch('workerTasks/save', updateData);
            if (response && response.status >= 200 && response.status < 300) {
              Swal.fire('Éxito', 'Trabajo/deuda marcado como pagado correctamente', 'success');
              await this.fetchData(null);
            } else {
              throw new Error(response?.data?.message || 'Error al actualizar');
            }
          } catch (error) {
            console.error('Error updating task:', error);
            Swal.fire('Error', 'No se pudo marcar como pagado', 'error');
            // Recargar para revertir el checkbox
            await this.fetchData(null);
          }
        } else {
          // Si cancela, recargar para mantener el estado
          await this.fetchData(null);
        }
      }
    },
    async deleteTask(id) {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: 'Esta acción no se puede deshacer',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      });

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('workerTasks/delete', id);
          Swal.fire('Éxito', 'Trabajo/deuda eliminado correctamente', 'success');
          await this.fetchData(null);
        } catch (error) {
          console.error('Error deleting task:', error);
          const errorMsg = error.response?.data?.message || error.message || 'No se pudo eliminar el trabajo/deuda';
          Swal.fire('Error', errorMsg, 'error');
        }
      }
    },
    async exportToExcel() {
      try {
        const filters = this.appendListFilters({});

        const response = await this.$store.dispatch('workerTasks/exportData', filters);
        
        // Crear un objeto URL temporal para el archivo Excel
        const url = window.URL.createObjectURL(new Blob([response.data]));
        
        // Crear un enlace temporal para descargar el archivo
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'trabajos_deudas.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Limpiar el URL temporal
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Éxito', 'Archivo Excel exportado correctamente', 'success');
      } catch (error) {
        console.error('Error al exportar:', error);
        Swal.fire('Error', 'No se pudo exportar el archivo Excel', 'error');
      }
    },
    async markAllPendingAsPaid() {
      const pendingTasks = this.data.filter(task => task.status === 'pending');
      
      if (pendingTasks.length === 0) {
        Swal.fire('Info', 'No hay tareas pendientes para marcar como pagadas', 'info');
        return;
      }

      const totalAmount = this.totalPendingEmployeeEarning;
      const count = pendingTasks.length;

      const result = await Swal.fire({
        title: '¿Marcar todas como pagadas?',
        html: `
          <div style="text-align: left;">
            <p><strong>Cantidad de tareas:</strong> ${count}</p>
            <p><strong>Total a pagar:</strong> ${totalAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p class="text-warning"><small>Se marcarán todas las tareas pendientes que están visibles en esta página.</small></p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, marcar todas como pagadas',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#28a745',
      });

      if (result.isConfirmed) {
        try {
          // Mostrar loading
          Swal.fire({
            title: 'Procesando...',
            text: 'Marcando tareas como pagadas',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          // Actualizar todas las tareas pendientes
          const updatePromises = pendingTasks.map(task => {
            const updateData = {
              id: task.id,
              total_paid: parseFloat(task.price),
              employee_paid: this.employeeEarning(task),
            };
            return this.$store.dispatch('workerTasks/save', updateData);
          });

          await Promise.all(updatePromises);

          Swal.fire('Éxito', `${count} tarea(s) marcada(s) como pagada(s) correctamente`, 'success');
          await this.fetchData(null);
        } catch (error) {
          console.error('Error updating tasks:', error);
          Swal.fire('Error', 'No se pudieron marcar todas las tareas como pagadas', 'error');
        }
      }
    },
  },
  async mounted() {
    try {
      await this.$store.dispatch('settings/get');
    } catch (error) {
      console.error('Error loading settings for worker tasks:', error);
    }
    this.taskForm.employee_percent = this.defaultEmployeePercent;
    if (this.isFieldWorker && this.currentUser?.id) {
      this.filters.user_id = this.currentUser.id;
    }
    if (!this.isFieldWorker) {
      await this.fetchUsers();
    }
    await this.fetchData(null);
  },
  beforeUnmount() {
    this.destroyOperarioSelect2();
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.page-item {
  margin: 0 5px;
  cursor: pointer;
  padding: 5px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
.page-item:hover:not(.active):not(:disabled) {
  background-color: #e9ecef;
}
.page-item.active {
  background-color: #5e72e4;
  color: white;
  border-color: #5e72e4;
}
.page-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cursor-pointer {
  cursor: pointer;
}
:deep(.select2-container) {
  width: 100% !important;
}
:deep(.select2-container--default .select2-selection--single) {
  height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
}
:deep(.select2-container--default .select2-selection--single .select2-selection__rendered) {
  line-height: calc(1.5em + 0.75rem);
  padding-left: 0.75rem;
}
</style>
